import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  Optional,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';

import { Subscription } from 'rxjs';

import { chartColorsSet } from '../../utils/colors-sets';
import { ComponentVariant, Variants } from '../variant/variant';
import { AvatarThemeSubject } from './avatar-theme.directive';

@Component({
  selector: 'rt-avatar',
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'variant',
  },
})
export class AvatarComponent implements OnChanges {
  public static readonly VARIANTS: Variants = [
    'oval',
    ['oval', 'rounded', 'large', 'large-x', 'small', 'medium', 'small-x'],
  ];
  @ComponentVariant(AvatarComponent.VARIANTS)
  @Input()
  public variant: string;
  @Input() public src: string;
  @Input() public online: boolean = false;
  @Input() public avatarTpl?: TemplateRef<any>;
  @Input() public backgroundMode: boolean = false;
  @Input() public hasColorBackground: boolean = false;
  @Input() public fullName: string;
  @Input() public imageStyles: object;
  @Input() public colorized: boolean = false;
  // TODO: Remove this after migration from backend generated avatar placeholders to frontend generated
  @Input() public backendStyleInitials: boolean = false;
  @Input() public hasUnreadNotifications: boolean = false;
  @Input() public tooltipTplRef: TemplateRef<any>;
  @Input() public iconName: string;
  @Input() public iconIsLight: boolean = false;

  @ViewChild('avatarPlaceholder') public avatarPlaceholder: ElementRef;

  public nameInitials: string;

  private themeClassSub: Subscription;
  private currentThemeClass: string;

  constructor(
    private elRef: ElementRef,
    private renderer2: Renderer2,
    @Optional() private themeClass$: AvatarThemeSubject,
  ) {}

  public ngAfterViewInit(): void {
    if (this.themeClass$) {
      this.themeClassSub = this.themeClass$.subscribe(className =>
        this.toggleThemeClass(className!),
      );
    }

    this.setBackgroundColor();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.fullName && this.fullName) {
      const [firstName, ...lastName] = this.fullName.trim().split(' ');
      const char1 = firstName.trim() ? firstName[0].trim().toUpperCase() : '';
      const char2 = lastName.length ? lastName.join('').trim()[0].toUpperCase() : '';
      this.nameInitials = char1 + char2;

      this.setBackgroundColor();
    }
  }

  public ngOnDestroy(): void {
    if (this.themeClassSub) {
      this.themeClassSub.unsubscribe();
    }
  }

  private toggleThemeClass(className: string): void {
    if (this.currentThemeClass) {
      this.renderer2.removeClass(this.elRef.nativeElement, this.currentThemeClass);
    }

    this.renderer2.addClass(this.elRef.nativeElement, className);
    this.currentThemeClass = className;
  }

  private setBackgroundColor(): void {
    if (
      !this.colorized ||
      !this.nameInitials ||
      !this.avatarPlaceholder ||
      !this.avatarPlaceholder.nativeElement
    )
      return;

    const strCharNo = parseInt(
      [...this.nameInitials]
        .reduce((acc, l) => acc + l.charCodeAt(0) / 2, 0)
        .toString(10)
        .slice(1, 2),
      10,
    );

    this.renderer2.setStyle(
      this.avatarPlaceholder.nativeElement,
      'background-color',
      chartColorsSet[strCharNo].backgroundColor,
    );
  }
}
